import { useState, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import JiraiProgressBar from './components/JiraiProgressBar';
import JiraiQuestionCard from './components/JiraiQuestionCard';
import JiraiCapsuleLoading from './components/JiraiCapsuleLoading';
import SeoHead from '@/components/base/SeoHead';
import {
  jiraiQuestions,
  calculateJiraiResult,
  getJiraiRandomDisplayOrder,
  JIRAI_REVERSE_ITEMS,
} from '@/mocks/jiraiQuestions';
import { jiraiResultTypes } from '@/mocks/jiraiResultTypes';

const TOTAL_QUESTIONS = 16;
const QUESTIONS_PER_PAGE = 4;
const TOTAL_PAGES = TOTAL_QUESTIONS / QUESTIONS_PER_PAGE;

export default function JiraiQuiz() {
  const navigate = useNavigate();

  const [currentPage, setCurrentPage] = useState(0);
  const [allAnswers, setAllAnswers] = useState<(number | null)[]>(
    Array(TOTAL_QUESTIONS).fill(null)
  );
  const [isAnimating, setIsAnimating] = useState(false);
  const [visible, setVisible] = useState(true);
  const [showCapsule, setShowCapsule] = useState(false);
  const [capsuleImageUrl, setCapsuleImageUrl] = useState('');
  const [pendingResultCode, setPendingResultCode] = useState('');

  const randomOrder = useMemo(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    return getJiraiRandomDisplayOrder();
  }, []);

  const currentPageRef = useRef(0);
  const allAnswersRef = useRef<(number | null)[]>(Array(TOTAL_QUESTIONS).fill(null));
  const isAnimatingRef = useRef(false);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);

  currentPageRef.current = currentPage;
  allAnswersRef.current = allAnswers;
  isAnimatingRef.current = isAnimating;

  const pageQuestions = useMemo(() => {
    const startIdx = currentPage * QUESTIONS_PER_PAGE;
    const endIdx = startIdx + QUESTIONS_PER_PAGE;
    return randomOrder.slice(startIdx, endIdx).map((questionId, idx) => ({
      questionId,
      displayNumber: startIdx + idx + 1,
      question: jiraiQuestions[questionId - 1],
      answer: allAnswers[questionId - 1] ?? null,
    }));
  }, [currentPage, allAnswers, randomOrder]);

  const answeredCount = allAnswers.filter((a) => a !== null).length;
  const isPageComplete = pageQuestions.every((q) => q.answer !== null);

  const handleSelect = (questionId: number, value: number) => {
    if (isAnimatingRef.current) return;

    const newAnswers = [...allAnswersRef.current];
    newAnswers[questionId - 1] = value;
    allAnswersRef.current = newAnswers;
    setAllAnswers(newAnswers);

    const startIdx = currentPageRef.current * QUESTIONS_PER_PAGE;
    const currentPageIds = randomOrder.slice(startIdx, startIdx + QUESTIONS_PER_PAGE);
    const currentIdxInPage = currentPageIds.indexOf(questionId);
    const nextUnansweredIdx = currentPageIds.findIndex(
      (id, i) => i > currentIdxInPage && newAnswers[id - 1] === null
    );

    if (nextUnansweredIdx !== -1 && cardRefs.current[nextUnansweredIdx]) {
      setTimeout(() => {
        cardRefs.current[nextUnansweredIdx]?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }, 80);
    }
  };

  const handleNextPage = () => {
    if (!isPageComplete || isAnimatingRef.current) return;

    const page = currentPageRef.current;
    const isLastPage = page === TOTAL_PAGES - 1;

    if (isLastPage) {
      setVisible(false);

      const finalAnswers = allAnswersRef.current.map((a) => (a === null ? 3 : a)) as number[];
      const resultCode = calculateJiraiResult(finalAnswers);

      const resultData = jiraiResultTypes[resultCode];
      const imgUrl = resultData?.imageUrl ?? '';
      setCapsuleImageUrl(imgUrl);
      setPendingResultCode(resultCode);
      setTimeout(() => setShowCapsule(true), 300);
    } else {
      isAnimatingRef.current = true;
      setIsAnimating(true);
      setVisible(false);

      setTimeout(() => {
        const nextPage = page + 1;
        currentPageRef.current = nextPage;
        setCurrentPage(nextPage);
        cardRefs.current = [];
        window.scrollTo({ top: 0, behavior: 'instant' });
        setVisible(true);
        isAnimatingRef.current = false;
        setIsAnimating(false);
      }, 260);
    }
  };

  const handleCapsuleComplete = () => {
    navigate(`/jirai-result/${pendingResultCode}`);
  };

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{
        fontFamily: "'M PLUS Rounded 1c', sans-serif",
        background: '#fff9fa',
      }}
    >
      <SeoHead
        title="診断スタート｜地雷タイプ診断"
        description="16問の質問に答えて、あなたの地雷タイプを診断しよう。約2分・完全無料。"
        canonical="/jirai-quiz"
        noIndex={true}
      />

      {/* 背景装飾 */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className="absolute inset-0"
          style={{
            background:
              'radial-gradient(ellipse at 20% 10%, rgba(225,29,72,0.06) 0%, transparent 55%), radial-gradient(ellipse at 80% 90%, rgba(225,29,72,0.04) 0%, transparent 55%)',
          }}
        />
      </div>

      {/* 固定ヘッダー */}
      <header
        className="fixed top-0 left-0 right-0 z-50 bg-white"
        style={{ borderBottom: '1px solid rgba(225,29,72,0.15)' }}
      >
        <div className="max-w-lg mx-auto px-4 py-3 flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 cursor-pointer"
            style={{ minHeight: '44px' }}
          >
            <div className="w-8 h-8 flex items-center justify-center">
              <i className="ri-arrow-left-line text-[#6b7280] text-base" />
            </div>
            <span className="text-[#1a1a2e] font-bold text-sm tracking-wide">
              地雷タイプ診断
            </span>
          </button>
          <span
            className="text-[10px] tracking-[0.25em] px-3 py-1 rounded-full"
            style={{
              color: '#e11d48',
              background: 'rgba(225,29,72,0.08)',
              border: '1px solid rgba(225,29,72,0.2)',
              fontFamily: "'M PLUS Rounded 1c', sans-serif",
            }}
          >
            {currentPage + 1} / {TOTAL_PAGES}
          </span>
        </div>
        <JiraiProgressBar current={answeredCount} total={TOTAL_QUESTIONS} />
      </header>

      {/* 設問エリア */}
      <div
        className="flex-1 px-4 pb-10 relative z-10 pt-[120px]"
        style={{
          opacity: visible ? 1 : 0,
          transform: visible ? 'translateY(0)' : 'translateY(14px)',
          transition: 'opacity 0.26s ease, transform 0.26s ease',
        }}
      >
        <div className="max-w-lg mx-auto space-y-4">
          {pageQuestions.map(({ questionId, displayNumber, question, answer }, idx) => (
            <div
              key={questionId}
              ref={(el) => { cardRefs.current[idx] = el; }}
            >
              <JiraiQuestionCard
                questionNumber={displayNumber}
                questionText={question.text}
                selectedValue={answer}
                onSelect={(value) => handleSelect(questionId, value)}
              />
            </div>
          ))}

          {/* 次へボタン */}
          <button
            onClick={handleNextPage}
            disabled={!isPageComplete}
            className="w-full py-4 rounded-xl font-bold text-base cursor-pointer transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
            style={{
              background: isPageComplete
                ? 'linear-gradient(135deg, #e11d48 0%, #9f1239 100%)'
                : 'rgba(225,29,72,0.12)',
              color: isPageComplete ? '#ffffff' : 'rgba(225,29,72,0.4)',
              fontFamily: "'M PLUS Rounded 1c', sans-serif",
              letterSpacing: '0.04em',
              border: 'none',
              marginTop: '24px',
            }}
          >
            {currentPage === TOTAL_PAGES - 1 ? '診断結果を見る' : '次のページへ'}
          </button>
        </div>
      </div>

      {showCapsule && (
        <JiraiCapsuleLoading
          imageUrl={capsuleImageUrl}
          onComplete={handleCapsuleComplete}
        />
      )}
    </div>
  );
}
